import { CategoricalTraitLevel } from 'libs/end-to-end/src/success-phenotype';
import { Trait } from 'libs/end-to-end/src/success-phenotype';
import { NumericalTraitLevel } from 'libs/end-to-end/src/success-phenotype';
import { ControlVariableDefinition } from 'libs/end-to-end/src/types/databases/controls/control-variable';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { sortWithReferenceOrderPlacingUnknownsLastAlphabetically } from 'libs/end-to-end/src/generic';

export interface TargetRangeProps {
  /**
   * What the endpoint at the left reads on the bottom (ie: <1)
   */
  leftEndpointLabel: string;

  /**
   * What the endpoint at the right reads on the bottom (ie: >5)
   */
  rightEndpointLabel: string;

  /**
   * The CSS value to give as the "left" property (when positioned absolutely over the entire bar) for range.
   */
  rangeCssLeftPercentage: number;

  /**
   * The CSS value to give as the "width" property (when positioned absolutely over the entire bar) for range.
   */
  rangeCssWidthPercentage: number;
}

/**
 * Get the target range props for a given trait and its control variable definitions.
 */
export const getTargetRangeProps = ({
  trait,
  controlVariableDefinitions,
}: {
  trait: Trait;
  controlVariableDefinitions: ControlVariableDefinition[];
}): TargetRangeProps | null => {
  if (trait.type === 'categorical') {
    return getTargetRangePropsForCategoricalCase({
      traitLevels: trait.levels,
      controlVariableDefinitions,
      traitControlVariableColumnHeaderValue:
        trait.controlVariableColumnHeaderValue,
    });
  }

  if (trait.type === 'numeric') {
    return getTargetRangePropsForNumericCase({
      traitLevels: trait.levels,
    });
  }
  throw guardExhaustive(trait);
};

const getTargetRangePropsForCategoricalCase = ({
  traitLevels,
  traitControlVariableColumnHeaderValue,
  controlVariableDefinitions,
}: {
  traitControlVariableColumnHeaderValue: string;
  traitLevels: CategoricalTraitLevel[];
  controlVariableDefinitions: ControlVariableDefinition[];
}): TargetRangeProps | null => {
  // note for categorical, minValue should be 1 as a number and maxValue should be equal to the number of bar charts that are present (aka levels)
  const ourVar = controlVariableDefinitions.find(
    (controlVariableDefinition) =>
      controlVariableDefinition.columnHeaderValue ===
      traitControlVariableColumnHeaderValue,
  );

  if (!ourVar) {
    return null;
  }

  const sortOrder = ourVar.categoricalTraitDefinition?.sortOrder;

  if (sortOrder === undefined) {
    return null;
  }

  const itemList: string[] = traitLevels.map((level) => level.value);

  const sortedList = sortWithReferenceOrderPlacingUnknownsLastAlphabetically({
    items: itemList,
    referenceOrder: sortOrder,
  });

  if (sortedList.length < 2) {
    return null;
  }

  const highestImpactLevel = traitLevels.reduce(
    (prev: CategoricalTraitLevel | null, current) => {
      if (
        current.percentage >
        (prev === null ? Number.NEGATIVE_INFINITY : prev.percentage)
      ) {
        return current;
      }
      return prev;
    },
    null,
  );

  if (!highestImpactLevel) {
    return null;
  }

  const indexOfHighestImpactLevel = sortedList.findIndex(
    (item) => item === highestImpactLevel.value,
  );
  const { rangeCssLeftPercentage, rangeCssWidthPercentage } =
    computeCssLeftAndWidthPercentages({
      minValue: 1,
      maxValue: sortedList.length,
      rangeStartValue: indexOfHighestImpactLevel + 1,
      rangeEndValue: indexOfHighestImpactLevel + 1,
    });
  return {
    leftEndpointLabel: sortedList[0],
    rightEndpointLabel: sortedList[sortedList.length - 1],
    rangeCssLeftPercentage,
    rangeCssWidthPercentage,
  };
};

const getTargetRangePropsForNumericCase = ({
  traitLevels,
}: {
  traitLevels: NumericalTraitLevel[];
}) => {
  const numericValues = computeMinAndMaxValuesForNumericCase({
    levels: traitLevels,
  });

  if (numericValues === null) {
    return null;
  }

  const {
    minValue,
    maxValue,
    rangeStartValue,
    rangeEndValue,
    leftEndpointLabel,
    rightEndpointLabel,
  } = numericValues;

  const { rangeCssLeftPercentage, rangeCssWidthPercentage } =
    computeCssLeftAndWidthPercentages({
      minValue,
      maxValue,
      rangeStartValue,
      rangeEndValue,
    });

  return {
    rangeCssLeftPercentage,
    rangeCssWidthPercentage,
    leftEndpointLabel,
    rightEndpointLabel,
  };
};

// small helper
const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v));

const computeCssLeftAndWidthPercentages = ({
  minValue,
  maxValue,
  rangeStartValue,
  rangeEndValue,
}: {
  minValue: number;
  maxValue: number;
  rangeStartValue: number;
  rangeEndValue: number;
}) => {
  const MIN_WIDTH_PERCENTAGE_FOR_RANGE_HIGHLIGHT = 12;

  const totalDisplayRange = Math.max(1e-6, maxValue - minValue);

  const leftPct = ((rangeStartValue - minValue) / totalDisplayRange) * 100;

  const widthPctRaw =
    ((rangeEndValue - rangeStartValue) / totalDisplayRange) * 100;

  // Prevent pathological over-100% widths if the denominator gets tiny.
  const widthPct = clamp(
    Math.max(widthPctRaw, MIN_WIDTH_PERCENTAGE_FOR_RANGE_HIGHLIGHT),
    MIN_WIDTH_PERCENTAGE_FOR_RANGE_HIGHLIGHT,
    100,
  );

  return {
    rangeCssLeftPercentage: clamp(leftPct, 0, 100 - widthPct),
    rangeCssWidthPercentage: widthPct,
  };
};

const computeMinAndMaxValuesForNumericCase = ({
  levels,
}: {
  levels: NumericalTraitLevel[];
}): null | {
  minValue: number;
  maxValue: number;
  rangeStartValue: number;
  rangeEndValue: number;
  leftEndpointLabel: string;
  rightEndpointLabel: string;
} => {
  type MMV = { value: number; continuous: boolean };
  let minValue: MMV | null = null;
  let maxValue: MMV | null = null;

  const updateMin = (value: number, continuous: boolean): MMV => {
    if (minValue === null || value <= minValue.value) {
      return {
        value,
        continuous:
          minValue === null
            ? continuous
            : (minValue.value === value && minValue.continuous) || continuous,
      };
    }
    return minValue;
  };

  const updateMax = (value: number, continuous: boolean): MMV => {
    if (maxValue === null || value >= maxValue.value) {
      return {
        value,
        continuous:
          maxValue === null
            ? continuous
            : (maxValue.value === value && maxValue.continuous) || continuous,
      };
    }
    return maxValue;
  };

  // Collect envelope from all levels
  for (const level of levels) {
    switch (level.type) {
      case 'greaterThan': // closed at value, open to +∞
        minValue = updateMin(level.value, false);
        maxValue = updateMax(level.value, true);
        break;
      case 'lessThanOrEqual': // open to -∞, closed at value
        minValue = updateMin(level.value, true);
        maxValue = updateMax(level.value, false);
        break;
      case 'twoSided': // closed interval
        minValue = updateMin(level.fromValue, false);
        maxValue = updateMax(level.toValue, false);
        break;
      default:
        throw guardExhaustive(level);
    }
  }
  if (minValue === null || maxValue === null) return null;

  // Widest concrete span (if any) to size our “soft infinity” pads.
  const widestTwoSided = levels
    .filter(
      (l): l is Extract<NumericalTraitLevel, { type: 'twoSided' }> =>
        l.type === 'twoSided',
    )
    .reduce((acc, l) => Math.max(acc, l.toValue - l.fromValue), 0);

  const closedSpan = maxValue.value - minValue.value;
  const baseSpan =
    closedSpan > 0 ? closedSpan : widestTwoSided > 0 ? widestTwoSided : 1;

  // Dominant range (for tucking logic)
  const highest = levels.reduce<NumericalTraitLevel | null>(
    (p, c) => (c.percentage > (p ? p.percentage : -Infinity) ? c : p),
    null,
  );
  if (!highest) return null;

  // Which sides are open?
  const leftOpen = !!minValue.continuous;
  const rightOpen = !!maxValue.continuous;

  // Soft-infinity padding (value units)
  const PAD_RATIO_BOTH_SIDES = 0.3; // per-side when both ends are open
  const PAD_RATIO_SINGLE = 0.4; // per-side when only one end is open
  const COLLAPSE_SYM_RATIO = 0.35; // symmetric pad when min==max
  const EDGE_TUCK_RATIO = 0.12; // tuck when dominant range meets a continuous edge
  const MIN_PAD_UNITS = 0.25; // absolute lower bound per side
  const MAX_PAD_RATIO = 0.6; // absolute upper bound per side

  const bothOpen = leftOpen && rightOpen;
  let leftPad = leftOpen
    ? (bothOpen ? PAD_RATIO_BOTH_SIDES : PAD_RATIO_SINGLE) * baseSpan
    : 0;
  let rightPad = rightOpen
    ? (bothOpen ? PAD_RATIO_BOTH_SIDES : PAD_RATIO_SINGLE) * baseSpan
    : 0;

  // If thresholds collapse to a single point (only '<=k' or only '>k'),
  // force *symmetric* context so normalization never degenerates.
  if (closedSpan === 0) {
    const sym = COLLAPSE_SYM_RATIO * baseSpan;
    leftPad = Math.max(leftPad, sym);
    rightPad = Math.max(rightPad, sym);
  }

  // Tuck pad on sides where the dominant range touches the continuous edge.
  const touchesLeft =
    (highest.type === 'twoSided' && highest.fromValue === minValue.value) ||
    highest.type === 'lessThanOrEqual';
  const touchesRight =
    (highest.type === 'twoSided' && highest.toValue === maxValue.value) ||
    highest.type === 'greaterThan';

  if (leftOpen && touchesLeft)
    leftPad = Math.min(leftPad, EDGE_TUCK_RATIO * baseSpan);
  if (rightOpen && touchesRight)
    rightPad = Math.min(rightPad, EDGE_TUCK_RATIO * baseSpan);

  // Clamp pads
  leftPad = leftOpen
    ? clamp(leftPad, MIN_PAD_UNITS, MAX_PAD_RATIO * baseSpan)
    : leftPad;
  rightPad = rightOpen
    ? clamp(rightPad, MIN_PAD_UNITS, MAX_PAD_RATIO * baseSpan)
    : rightPad;

  // Final display bounds
  const displayMin = minValue.value - leftPad;
  const displayMax = maxValue.value + rightPad;

  // Build highlight from dominant level
  let rangeStartValue: number;
  let rangeEndValue: number;
  switch (highest.type) {
    case 'twoSided':
      rangeStartValue = highest.fromValue;
      rangeEndValue = highest.toValue;
      break;
    case 'greaterThan':
      rangeStartValue = highest.value;
      rangeEndValue = displayMax;
      break;
    case 'lessThanOrEqual':
      rangeStartValue = displayMin;
      rangeEndValue = highest.value;
      break;
    default:
      throw guardExhaustive(highest);
  }

  return {
    minValue: displayMin,
    maxValue: displayMax,
    rangeStartValue,
    rangeEndValue,
    leftEndpointLabel: `${minValue.continuous ? '<' : ''}${minValue.value}`,
    rightEndpointLabel: `${maxValue.continuous ? '>' : ''}${maxValue.value}`,
  };
};
